import React, { ReactElement } from 'react';
import { NavBarLinkItem, NavigationBarWithProfile } from '@atlassian/learning-components';
import { UITrackEventPayload } from '@atlassian/community-library';

import { getAssetUrl } from 'src/services/http/user';
import { getAdobeCourseCatalogUrl } from 'src/services/http/adobe';
import { getTenantBaseUrl, TENANTS } from 'src/utils/tenant';

const ENABLEMENT_LOGO = '/images/enablement/enablement-academy-logo.png';

/**
 * Gets the navigation items for Enablement Academy.
 * Computed at render time to ensure correct environment variables during SSR.
 */
const getEnablementNavItems = (): Array<{ text: string; href: string; isSelected?: boolean }> => {
  return [
    { text: 'Course catalog', href: getAdobeCourseCatalogUrl() },
    { text: 'Partner Central', href: 'https://partnercentral.atlassian.com' },
    { text: 'Atlassian Learning', href: getTenantBaseUrl(TENANTS.COMMUNITY) || '' },
  ];
};

const createEnablementAcademyLogo = (): ReactElement => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
    <a href="/" style={{ textDecoration: 'none' }}>
      <img src={getAssetUrl(ENABLEMENT_LOGO)} alt="Enablement Academy" style={{ height: '32px', width: 'auto' }} />
    </a>
  </div>
);

const createLeftItems = (items: Array<{ text: string; href: string; isSelected?: boolean }>): ReactElement[] =>
  items.map((item) => (
    <NavBarLinkItem key={item.href} link={{ text: item.text, href: item.href }} isSelected={false} />
  ));

interface EnablementNavigationProps {
  signupHref: string;
  currentApp: string;
  isLoggedIn: boolean;
  user: {
    id: string;
    publicName: string;
    avatar: string;
  };
  loginHref: string;
  logoutHref: string;
  isSticky?: boolean;
  switchHref: string;
  showNewMenu?: boolean;
  onAnalyticsSendEvent?: (event: string, payload: UITrackEventPayload) => void;
}

export const EnablementNavigation: React.FC<EnablementNavigationProps> = ({
  currentApp,
  isLoggedIn,
  user,
  loginHref,
  logoutHref,
  isSticky = false,
  showNewMenu = false,
  onAnalyticsSendEvent,
}) => {
  // Compute nav items at render time to ensure correct environment during SSR - needs to be within the component
  const navItems = getEnablementNavItems();

  return (
    <NavigationBarWithProfile
      logoItem={createEnablementAcademyLogo()}
      leftItems={createLeftItems(navItems)}
      isLoggedIn={isLoggedIn}
      user={user}
      loginHref={loginHref}
      logoutHref={logoutHref}
      currentApp={currentApp}
      isSticky={isSticky}
      showNewMenu={showNewMenu}
      onAnalyticsSendEvent={onAnalyticsSendEvent}
    />
  );
};
